import type {StoredReturnRoute} from '@/composables/useReturnRoute/useReturnRoute';
import type {CarrierId} from '@myparcel-frontend/core-api-service';
import type {Shipment} from '@myparcel-frontend/order-service';
import type {GetShipmentLabelsParameters} from '@myparcel-frontend/sdk';
import {isRowShipment} from '@myparcel-frontend/address';
import {useCoreApiService} from '@myparcel-frontend/core-api-service';
import {useNotification} from '@myparcel-frontend/notifications';
import {PaymentStatus} from '@myparcel-frontend/order-service';
import {isBol, toCarrierId} from '@myparcel-frontend/orders';
import {useSdk} from '@myparcel-frontend/sdk';
import {PaperSize, PrintLabelPosition} from '@myparcel-frontend/types';
import {dayjs, file} from '@myparcel-frontend/utils';
import {attempt, attemptAsync} from 'es-toolkit';
import {watch} from 'vue';
import {isValidPaymentUrl} from './paymentUrl';
import useBilling from '../useBilling/useBilling';
import {usePrintLabelModal} from '../usePrintLabelModal/usePrintLabelModal';
import useShipments from '../useShipments';
import useShop from '../useShop/useShop';
import useUiSettings from '../useUiSettings';
import {useLoader} from '@/components/ui/Loader';
import useCarriers from '@/composables/useCarriers/useCarriers';
import {useDirectPrinting} from '@/composables/useDirectPrinting/useDirectPrinting';
import {usePrintOrDownloadMode} from '@/composables/usePrintOrDownloadMode/usePrintOrDownloadMode';
import {useReturnRoute} from '@/composables/useReturnRoute/useReturnRoute';
import logToMonitor from '@/utils/logging';

export const useShipmentLabel = () => {
  const {sdk} = useSdk();
  const {getShipmentLabels} = useCoreApiService();
  const {pendingPrintJobs, directPrintingIsActive, printerGroups, getPrinterGroups} = useDirectPrinting();
  const {isPrintMode} = usePrintOrDownloadMode();
  const {addNotification} = useNotification();
  const {openPrintModal, isPrintModalOpen, confirmedDownloadSettings} = usePrintLabelModal();
  const {currentShop} = useShop();
  const {stopLoading} = useLoader();
  const {getShipmentsById: getCoreApiShipmentsById} = useShipments();
  const {isPostPayment, isPrepaid} = useBilling();
  const {uiSettings} = useUiSettings();
  const {setReturnRoute} = useReturnRoute();
  const {getShopCarrierOptions} = useCarriers();

  const shouldShowPrintModal = (): boolean => {
    return !currentShop.value.general_settings.label_format_locked;
  };

  /**
   * TODO: SB-2679
   * Shipment is not downloadable if the shipment status is CANCELLED
   * Shipment is not downloadable if the shipment is a label package
   * Shipment is not downloadable if the shipment is secondary
   */
  const isShipmentDownloadable = async (shipment: Shipment): Promise<boolean> => {
    const shopCarriers = await getShopCarrierOptions();
    const activeCarrierIds = shopCarriers.value
      .filter((carrierOption) => carrierOption.enabled)
      .map((carrierOption) => carrierOption.carrier.id);

    const carrierId = toCarrierId(shipment.carrier);

    // You cannot download a label when the shipment has an inactive carrier. The api won't allow it.
    if (!activeCarrierIds.includes(carrierId)) {
      return false;
    }

    const isPrinterless = shipment.options?.printReturnLabelAtDropOff;

    if (isPrinterless) {
      return false;
    }

    const paymentStatus = shipment.label?.paymentStatus;
    const isPaid = paymentStatus === PaymentStatus.PAID;
    const postPayment = isPostPayment(currentShop.value.billing.billing_method);
    const isSentByBol = isBol(shipment?.carrier);

    return isPaid || postPayment || isSentByBol;
  };

  /**
   * TODO: SB-2679
   */
  const isShipmentPayable = async (shipment: Shipment, activeCarrierIds?: CarrierId[]): Promise<boolean> => {
    const shopCarriers = await getShopCarrierOptions();
    const resolvedCarrierIds =
      activeCarrierIds ??
      shopCarriers.value
        .filter((carrierOption) => carrierOption.enabled)
        .map((carrierOption) => carrierOption.carrier.id);

    const carrierId = toCarrierId(shipment.carrier);

    // You cannot download a label when the shipment has an inactive carrier. The api won't allow it.
    if (!resolvedCarrierIds.includes(carrierId)) {
      return false;
    }

    return (
      isPrepaid(currentShop.value.billing.billing_method) &&
      shipment.label?.paymentStatus !== PaymentStatus.PAID &&
      !isBol(shipment?.carrier)
    );
  };

  // The order-service doesn't expose region, so we probe core-api to detect Rest Of World shipments.
  const checkAnyShipmentIsRestOfWorld = async (shipments: Shipment[]): Promise<boolean> => {
    const ids = shipments.map((shipment) => shipment.id.toString());

    const [error, coreApiShipmentsResponse] = await attemptAsync(() => getCoreApiShipmentsById(ids));

    if (error || !coreApiShipmentsResponse) {
      logToMonitor(error as Error, {
        _myparcel_extra_info:
          'Error getting shipments from core-api when checking for Rest Of World shipments before printing labels',
        _myparcel_shipment_ids: ids.join(';'),
      });
      return false;
    }

    return coreApiShipmentsResponse.shipments.some((shipment) => isRowShipment(String(shipment.region)));
  };

  // Opens the print modal, waits for user action, returns chosen params or null if canceled.
  const resolvePrintSettings = async (
    shipments: Shipment[],
    hasRestOfWorldShipment: boolean,
  ): Promise<GetShipmentLabelsParameters | null> => {
    stopLoading();
    confirmedDownloadSettings.value = undefined;
    openPrintModal(shipments, hasRestOfWorldShipment);

    await new Promise<void>((resolve) => {
      const unwatch = watch(
        isPrintModalOpen,
        (open) => {
          if (!open) {
            unwatch();
            resolve();
          }
        },
        {immediate: true},
      );
    });

    const settings = confirmedDownloadSettings.value;
    confirmedDownloadSettings.value = undefined;

    if (!settings) {
      return null;
    }

    return {
      format: settings.labelFormat,
      positions: settings.positions.join(';'),
    };
  };

  const savePrintInstructions = (shipmentIds: string[], printerOptions: GetShipmentLabelsParameters) => {
    localStorage.setItem(
      'afterPayment',
      JSON.stringify({
        printerOptions,
        shipmentIds,
      }),
    );
  };

  const fetchShipmentLabelBlob = async (
    shipmentIds: string[],
    parameters: GetShipmentLabelsParameters,
    options: {
      suppressNotification?: boolean;
      skipPaymentRedirect?: boolean;
      returnRoute?: StoredReturnRoute;
    } = {},
  ): Promise<Blob | void> => {
    const {suppressNotification = false, skipPaymentRedirect = false, returnRoute} = options;

    const [error, label] = await attemptAsync(() =>
      sdk.getShipmentLabels({
        path: {ids: shipmentIds.join(';')},
        parameters,
      }),
    );

    if (error || !label) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Error getting and downloading shipment labels',
        _myparcel_shipment_ids: shipmentIds,
        _myparcel_parameters: parameters,
      });
      if (!suppressNotification) {
        addNotification({
          variant: 'alert',
          body: 'printing.notifications.error',
        });
      }
      throw error;
    }

    if (label instanceof Blob) {
      return label;
    }

    const paymentInstructions = label[0];

    // Empty array or missing first entry: backend responded with a payment-instructions envelope
    // but no actual instruction. Without this guard the caller sees nothing happen on download.
    if (!paymentInstructions) {
      logToMonitor(new Error('Empty payment_instructions array'), {
        _myparcel_extra_info: 'SDK returned a payment-instructions response with no entries',
        _myparcel_shipment_ids: shipmentIds,
      });
      if (!suppressNotification) {
        addNotification({
          variant: 'alert',
          body: 'printing.notifications.error',
        });
      }
      return;
    }

    // Called from the post-payment resume path. Another 402 here means payment is still pending —
    // redirecting again would loop the user through the payment provider. Surface an error instead.
    if (skipPaymentRedirect) {
      logToMonitor(new Error('Payment still required after resume'), {
        _myparcel_extra_info: 'Second 402 received during post-payment resume — not redirecting',
        _myparcel_shipment_ids: shipmentIds,
      });
      addNotification({
        variant: 'alert',
        body: 'printing.notifications.error',
      });
      return;
    }

    // Never assign an attacker-controlled URL to `window.location`. Gate on https + known payment
    // provider host to prevent XSS via `javascript:` / `data:` and open-redirect to arbitrary sites.
    if (!isValidPaymentUrl(paymentInstructions.payment_url)) {
      logToMonitor(new Error('Rejected payment redirect to untrusted URL'), {
        _myparcel_extra_info: 'Payment URL failed allowlist validation',
        _myparcel_shipment_ids: shipmentIds,
      });
      if (!suppressNotification) {
        addNotification({
          variant: 'alert',
          body: 'printing.notifications.error',
        });
      }
      return;
    }

    // Save where to send the user after they return from the provider; the global returnRouteGuard
    // performs the redirect on re-entry. `undefined` captures the current route.
    setReturnRoute(returnRoute);
    savePrintInstructions(shipmentIds, parameters);
    window.location.assign(paymentInstructions.payment_url);
  };

  const saveLabelBlob = async (label: Blob): Promise<void> => {
    await file.printOrDownload(label, {
      shouldPrint: isPrintMode.value,
      filename: `myparcel-label-${dayjs().format('YYYYMMDDHHmmss')}.pdf`,
    });
  };

  const findSelectedPrinterGroup = async (printerGroupId: string) => {
    await attemptAsync(() => getPrinterGroups());
    return printerGroups.value.find((group) => group.id === printerGroupId);
  };

  const getPrinterIdForFormat = (
    printerGroup: NonNullable<Awaited<ReturnType<typeof findSelectedPrinterGroup>>>,
    format: GetShipmentLabelsParameters['format'],
  ): string | undefined => {
    return format === PaperSize.a4 ? printerGroup.a4PrinterId : printerGroup.a6PrinterId;
  };

  const printDirectly = async (
    shipmentIds: string[],
    parameters: GetShipmentLabelsParameters,
    printerGroupId: string,
    suppressNotification: boolean,
  ): Promise<boolean> => {
    const [error, api] = await attemptAsync(() =>
      getShipmentLabels(shipmentIds, parameters, {
        useDirectPrinting: true,
        printerGroupId,
      }),
    );

    if (error || !api?.data) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Error getting and downloading shipment labels',
        _myparcel_shipment_ids: shipmentIds,
        _myparcel_parameters: parameters,
      });
      if (!suppressNotification) {
        addNotification({
          variant: 'alert',
          body: 'printing.notifications.error',
        });
      }
      throw error;
    }

    pendingPrintJobs.value.push(api.response.headers.get('x-print-request-id') ?? '');
    return true;
  };

  /**
   * Handles the direct-printing route. Returns the print result when direct printing applies, or
   * `null` when it does not so the caller falls back to fetching the label for download.
   */
  const tryDirectPrint = async (
    shipmentIds: string[],
    parameters: GetShipmentLabelsParameters,
    suppressNotification: boolean,
  ): Promise<boolean | null> => {
    const printerGroupId = uiSettings.value?.preferred_printer_group_id;

    if (!directPrintingIsActive.value || !printerGroupId) {
      return null;
    }

    const selectedGroup = await findSelectedPrinterGroup(printerGroupId);

    if (selectedGroup && !getPrinterIdForFormat(selectedGroup, parameters.format)) {
      const isA4 = parameters.format === PaperSize.a4;
      addNotification({
        variant: 'alert',
        body: isA4 ? 'printing.notifications.noA4Printer' : 'printing.notifications.noA6Printer',
      });
      return false;
    }

    return printDirectly(shipmentIds, parameters, printerGroupId, suppressNotification);
  };

  const downloadShipmentLabels = async (
    shipments: Shipment[],
    parameters: GetShipmentLabelsParameters = {
      positions: PrintLabelPosition.topLeft,
      format: PaperSize.a4,
    },
    options: {
      suppressNotification?: boolean;
      returnRoute?: StoredReturnRoute;
      skipPrintModal?: boolean;
    } = {},
  ): Promise<boolean> => {
    const {suppressNotification = false, returnRoute, skipPrintModal = false} = options;
    let finalParameters = parameters;

    const [error, result] = await attemptAsync<boolean, Error>(async () => {
      if (!skipPrintModal && shouldShowPrintModal()) {
        const hasRow = await checkAnyShipmentIsRestOfWorld(shipments);
        const chosenParams = await resolvePrintSettings(shipments, hasRow);

        if (!chosenParams) {
          return false;
        }

        finalParameters = chosenParams;
      }

      const shipmentIds = shipments.map((shipment) => shipment.id);

      const directPrintResult = await tryDirectPrint(shipmentIds, finalParameters, suppressNotification);

      if (directPrintResult !== null) {
        return directPrintResult;
      }

      const label = await fetchShipmentLabelBlob(shipmentIds, finalParameters, {
        suppressNotification,
        returnRoute,
      });

      // No label when `fetchShipmentLabelBlob` hit the 402 branch and redirected to the payment provider.
      if (!label) {
        return false;
      }

      await saveLabelBlob(label);

      return true;
    });

    if (error) {
      throw error;
    }

    // On the success path `attemptAsync` guarantees a boolean; the error path already threw above.
    return result;
  };

  /**
   * Resumes the label download flow after the user returns from the payment provider.
   *
   * Always clears `afterPayment` up front so a corrupt or stale payload cannot re-trigger the
   * resume on subsequent mounts. Passes `skipPaymentRedirect=true` to `fetchShipmentLabelBlob`
   * so a second 402 surfaces as an error rather than another redirect — preventing loops when
   * payment was abandoned or is still pending provider-side.
   */
  const continueWithLabelDownload = async () => {
    const afterPaymentData = localStorage.getItem('afterPayment');

    if (!afterPaymentData) {
      return;
    }

    // Always clear before doing anything else; stale or malformed state should not persist.
    localStorage.removeItem('afterPayment');

    const [parseError, parsed] = attempt<{printerOptions?: unknown; shipmentIds?: unknown}, Error>(() =>
      JSON.parse(afterPaymentData),
    );

    if (parseError) {
      addNotification({
        variant: 'alert',
        body: 'printing.notifications.error',
      });

      logToMonitor(parseError, {
        _myparcel_extra_info: 'Corrupt afterPayment localStorage payload — could not JSON.parse',
      });
      return;
    }

    const shipmentIds = parsed.shipmentIds;
    const printerOptions = parsed.printerOptions;
    const hasValidShape =
      Array.isArray(shipmentIds) &&
      shipmentIds.length > 0 &&
      shipmentIds.every((id) => typeof id === 'string') &&
      !!printerOptions &&
      typeof printerOptions === 'object';

    if (!hasValidShape) {
      addNotification({
        variant: 'alert',
        body: 'printing.notifications.error',
      });

      logToMonitor(new Error('Invalid afterPayment shape'), {
        _myparcel_extra_info: 'afterPayment localStorage payload did not match expected shape',
      });
      return;
    }

    const [error, label] = await attemptAsync(() =>
      fetchShipmentLabelBlob(shipmentIds, printerOptions as GetShipmentLabelsParameters, {
        suppressNotification: true,
        skipPaymentRedirect: true,
      }),
    );

    if (error) {
      addNotification({
        variant: 'alert',
        body: 'printing.notifications.error',
      });

      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Failed to fetch label during post-payment resume',
        _myparcel_shipment_ids: shipmentIds,
      });
      return;
    }

    if (label) {
      await saveLabelBlob(label);
    }
  };

  return {
    isShipmentDownloadable,
    isShipmentPayable,
    shouldShowPrintModal,
    checkAnyShipmentIsRestOfWorld,
    resolvePrintSettings,
    fetchShipmentLabelBlob,
    saveLabelBlob,
    downloadShipmentLabels,
    continueWithLabelDownload,
  };
};
